import { useSelector, useDispatch } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { selectDrugAdviceEntity } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchDrugAdviceEntity } from 'src/clinic/reducers/prescription/services/drugAdviceItems'
import {
  HE_DRUG_ADVICE_DOSAGE,
  HE_DRUG_ADVICE_DURATION,
  HE_DRUG_ADVICE_NOTE,
  HE_DRUG_ADVICE_FREQUENCY,
  HE_DRUG_ADVICE_TIMING,
  HE_DRUG_ADVICE_TYPE,
} from 'src/consumer/constants'

/**
 * Custom hook for managing drug advice data from Redux store
 * @param {string} clinicBrandId - Clinic brand ID for fetching data
 * @returns {Object} Drug advice data and functions
 */
export const useDrugAdviceData = (clinicBrandId) => {
  const dispatch = useDispatch()

  // Redux selectors for all drug advice entities
  const drugAdviceDosagesData = useSelector(selectDrugAdviceEntity(HE_DRUG_ADVICE_DOSAGE))
  const drugAdviceNotesData = useSelector(selectDrugAdviceEntity(HE_DRUG_ADVICE_NOTE))
  const drugAdviceDurationsData = useSelector(selectDrugAdviceEntity(HE_DRUG_ADVICE_DURATION))
  const drugAdviceFrequencyData = useSelector(selectDrugAdviceEntity(HE_DRUG_ADVICE_FREQUENCY))
  const drugAdviceWhatTimeData = useSelector(selectDrugAdviceEntity(HE_DRUG_ADVICE_TIMING))
  const drugAdviceTypesData = useSelector(selectDrugAdviceEntity(HE_DRUG_ADVICE_TYPE))

  // Extract data arrays with fallbacks
  const drugAdviceDosages = useMemo(() => drugAdviceDosagesData?.data || [], [drugAdviceDosagesData])
  const drugAdviceNotes = useMemo(() => drugAdviceNotesData?.data || [], [drugAdviceNotesData])
  const drugAdviceDurations = useMemo(() => drugAdviceDurationsData?.data || [], [drugAdviceDurationsData])
  const drugAdviceFrequencies = useMemo(() => drugAdviceFrequencyData?.data || [], [drugAdviceFrequencyData])
  const drugAdviceWhatTimeOptions = useMemo(() => drugAdviceWhatTimeData?.data || [], [drugAdviceWhatTimeData])
  const drugAdviceTypes = useMemo(() => drugAdviceTypesData?.data || [], [drugAdviceTypesData])

  // Loading states
  const loading = useMemo(() => ({
    dosages: drugAdviceDosagesData?.state === 'loading',
    notes: drugAdviceNotesData?.state === 'loading',
    durations: drugAdviceDurationsData?.state === 'loading',
    frequencies: drugAdviceFrequencyData?.state === 'loading',
    timing: drugAdviceWhatTimeData?.state === 'loading',
    types: drugAdviceTypesData?.state === 'loading',
  }), [
    drugAdviceDosagesData,
    drugAdviceNotesData,
    drugAdviceDurationsData,
    drugAdviceFrequencyData,
    drugAdviceWhatTimeData,
    drugAdviceTypesData
  ])

  // Individual fetch functions
  const getDrugAdviceNotes = useCallback(async () => {
    if (clinicBrandId) {
      dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_NOTE }))
    }
  }, [dispatch, clinicBrandId])

  const getDrugAdviceDuration = useCallback(async () => {
    if (clinicBrandId) {
      dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_DURATION }))
    }
  }, [dispatch, clinicBrandId])

  const getDrugAdviceDosage = useCallback(async () => {
    if (clinicBrandId) {
      dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_DOSAGE }))
    }
  }, [dispatch, clinicBrandId])

  const getDrugAdviceFreq = useCallback(async () => {
    if (clinicBrandId) {
      dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_FREQUENCY }))
    }
  }, [dispatch, clinicBrandId])

  const getDrugAdviceWhen = useCallback(async () => {
    if (clinicBrandId) {
      dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_TIMING }))
    }
  }, [dispatch, clinicBrandId])

  const getDrugInfoTypes = useCallback(async () => {
    if (clinicBrandId) {
      dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_TYPE }))
    }
  }, [dispatch, clinicBrandId])

  // Fetch all drug advice data
  const fetchAllDrugAdviceData = useCallback(async () => {
    if (!clinicBrandId) return

    await Promise.all([
      getDrugAdviceNotes(),
      getDrugAdviceDuration(),
      getDrugAdviceDosage(),
      getDrugAdviceFreq(),
      getDrugAdviceWhen(),
      getDrugInfoTypes(),
    ])
  }, [
    getDrugAdviceNotes,
    getDrugAdviceDuration,
    getDrugAdviceDosage,
    getDrugAdviceFreq,
    getDrugAdviceWhen,
    getDrugInfoTypes,
  ])

  // Overall loading state
  const isLoading = useMemo(() => {
    return Object.values(loading).some(Boolean)
  }, [loading])

  return {
    // Data arrays
    drugAdviceDosages,
    drugAdviceNotes,
    drugAdviceDurations,
    drugAdviceFrequencies,
    drugAdviceWhatTimeOptions,
    drugAdviceTypes,

    // Loading states
    loading,
    isLoading,

    // Individual fetch functions
    getDrugAdviceNotes,
    getDrugAdviceDuration,
    getDrugAdviceDosage,
    getDrugAdviceFreq,
    getDrugAdviceWhen,
    getDrugInfoTypes,

    // Fetch all function
    fetchAllDrugAdviceData,
  }
}

export default useDrugAdviceData